import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { fireStore } from "../../firebase";
import { useAppSelector } from "../storeHooks";
import { messagingDocTimestampMillis, serializeMessagingDocTimestamp } from "./serializeMessagingDocTimestamp";

export type VendorMessagingSignal = {
	eventsChanged: boolean;
	chatStateOnly: boolean;
	chatStateUpdatedAt: number | null;
};

export const useOnUpdateVendorMessagingSignals = (
	shouldStartUpdating: boolean,
	onSignal: (signal: VendorMessagingSignal) => void,
) => {
	const userId = useAppSelector((state) => state.user?.userDetails?.id);
	const eventsSerializedRef = useRef<string | null>(null);
	const chatStateSerializedRef = useRef<string | null>(null);
	const seededRef = useRef(false);
	const onSignalRef = useRef(onSignal);
	onSignalRef.current = onSignal;

	useEffect(() => {
		if (!fireStore || !userId || !shouldStartUpdating) return;

		seededRef.current = false;
		eventsSerializedRef.current = null;
		chatStateSerializedRef.current = null;

		const documentRef = doc(fireStore, "vendorsCollectionForMessaging", userId);
		const unsubscribe = onSnapshot(documentRef, (docSnap) => {
			if (!docSnap.exists()) return;
			const data = docSnap.data();
			const nextEvents = serializeMessagingDocTimestamp(data.eventsUpdatedAt);
			const nextChatState = serializeMessagingDocTimestamp(data.chatStateUpdatedAt);

			if (!seededRef.current) {
				seededRef.current = true;
				eventsSerializedRef.current = nextEvents;
				chatStateSerializedRef.current = nextChatState;
				return;
			}

			const eventsChanged = nextEvents !== eventsSerializedRef.current;
			const chatStateChanged = nextChatState !== chatStateSerializedRef.current;
			if (!eventsChanged && !chatStateChanged) return;

			eventsSerializedRef.current = nextEvents;
			chatStateSerializedRef.current = nextChatState;

			if ((eventsChanged && nextEvents) || chatStateChanged) {
				onSignalRef.current({
					eventsChanged,
					chatStateOnly: !eventsChanged,
					chatStateUpdatedAt: chatStateChanged ? messagingDocTimestampMillis(data.chatStateUpdatedAt) : null,
				});
			}
		});

		return () => unsubscribe();
	}, [userId, shouldStartUpdating]);
};
